import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatHint, MatLabel } from '@angular/material/form-field';
import { SiaAiTextButtonComponent } from '../../ai-prompt/ai-text-button/ai-text-button.component';
import {
  EnvironmentService,
  FileService,
  SiaArchivePath,
  SiaTranslatePipe,
} from '@sia/shared';
import { Guid } from 'guid-typescript';
import $ from 'jquery';
import {
  Editor,
  NgxEditorComponent,
  NgxEditorMenuComponent,
  Toolbar,
} from 'ngx-editor';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';
import { SiaErrorHintComponent } from '../sia-error-hint/sia-error-hint.component';

@Component({
  selector: 'sia-html-editor',
  standalone: true,
  imports: [
    NgxEditorComponent,
    NgxEditorMenuComponent,
    MatLabel,
    FormsModule,
    SiaErrorHintComponent,
    CommonModule,
    SiaTranslatePipe,
    MatHint,
    SiaAiTextButtonComponent,
  ],
  templateUrl: './sia-html-editor-box.component.html',
  styleUrl: './sia-html-editor-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaHtmlEditorComponent extends SiaBaseEditorSignalComponent {
  private siaFileUtilsService = inject(FileService);
  environmentService = inject(EnvironmentService);
  @ViewChild('labelElement', { static: false }) labelElement?: ElementRef;
  readonly plainTextChange = output<string>();
  readonly textAreaHeight = input<any>(100);

  readonly editorValue = signal<string>('');
  private readonly viewReady = signal(false);
  private _allowUpdate: boolean = false;
  private _valueChangeTimeout: any;

  get labelWidth(): number {
    return this.labelElement?.nativeElement.offsetWidth ?? 4;
  }

  constructor() {
    super();
    this.editor = new Editor({ parseOptions: { preserveWhitespace: 'full' } });

    effect(
      () => {
        const incomingValue = this.value() ?? '';
        const containerWidth = $('.sia-html-editor-container')?.width() ?? 700;
        const percentWidthRegex = /style="width: (\d+)%[^"]*"/g;
        const htmlValueWidthPx = incomingValue.replace(
          percentWidthRegex,
          (match: any, p1: any) => {
            const widthInPercent = parseInt(p1, 10);
            const widthInPx = Math.round(
              (widthInPercent / 100) * containerWidth,
            );
            return match.replace(
              /style="width: \d+%[^"]*"/,
              `width="${widthInPx}px"`,
            );
          },
        );

        this.editorValue.set(htmlValueWidthPx);
        this.editor.setContent(htmlValueWidthPx);

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlValueWidthPx, 'text/html');
        const plainText = doc.body.innerText;
        this.plainTextChange.emit(plainText);
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const height = this.textAreaHeight();
      if (!this.viewReady()) {
        return;
      }
      this.applyTextAreaHeight(height);
    });
  }
  keyDownEvent() {
    this._allowUpdate = true;
  }
  editor!: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];
  async processImages(htmlValue: string) {
    if (!htmlValue) {
      return;
    }
    clearTimeout(this._valueChangeTimeout);
    this._valueChangeTimeout = setTimeout(async () => {
      const imgTagRegex =
        /<img[^>]+src="data:image\/[^;]+;base64,([^"]+)"[^>]*>/g;
      let match;
      let srcChanged = false;
      while ((match = imgTagRegex.exec(htmlValue)) !== null) {
        const base64String = match[1];
        // Save base64String to the database
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const mimeType = match[0].match(/data:([^;]+);base64/)![1];
        const blob = new Blob([byteArray], { type: mimeType });
        // Now you can use the blob, for example, upload it to the server
        var blobName: string =
          Guid.create().toString() + '.' + mimeType.split('/')[1];
        const archivePath: SiaArchivePath = {
          path: 'HtmlEditor',
          isRelativePath: true,
          isSystemArchive: false,
          newName: blobName,
        };
        var imgUrl = await this.siaFileUtilsService.uploadFile(
          archivePath,
          blob,
          true,
        );
        htmlValue = htmlValue.replace(
          `data:${mimeType};base64,${base64String}`,
          imgUrl,
        );
        srcChanged = true;
      }
      let containerWidth = $('.sia-html-editor-container')?.width() ?? 700;
      let a4Width = 700;
      const widthRegex = /<img[^>]*width="(\d+)px"[^>]*>/g;
      let htmlValueWidthPercent = htmlValue.replace(widthRegex, (match, p1) => {
        const widthInPx = parseInt(p1, 10);
        let widthInPercent = (widthInPx / containerWidth) * 100;
        widthInPercent = Math.round(widthInPercent);
        // widthInPercent : containerWidht = a4PercentWidth : a4Width
        let a4PxWidth = (widthInPercent * a4Width) / 100;
        return match.replace(
          `width="${p1}px"`,
          `style="width: ${widthInPercent}%; max-width: ${widthInPercent}%; $a4reportwidth: ${a4PxWidth}px;"`,
        );
      });
      const imgTagWithoutStyleRegex = /<img((?!style)[^>])+>/g;
      htmlValueWidthPercent = htmlValueWidthPercent.replace(
        imgTagWithoutStyleRegex,
        (match) => {
          return match.replace(
            `<img`,
            `<img style="width: 50%; max-width: 50%; $a4reportwidth: ${
              a4Width / 2
            }px;"`,
          );
        },
      );
      const currentValue = this.value() ?? '';
      if (srcChanged || (!currentValue && htmlValue)) {
        this.editor.setContent(htmlValue);
        this.editorValue.set(htmlValue);
      }
      if (this._allowUpdate) {
        this.value.set(htmlValueWidthPercent);
        this._allowUpdate = false;
      }
    }, 300);
  }
  ngOnDestroy(): void {
    clearTimeout(this._valueChangeTimeout);
    this.editor.destroy();
    super.ngOnDestroy();
  }
  allowUpdate() {
    this._allowUpdate = true;
  }
  ngAfterViewInit() {
    this.viewReady.set(true);
    this.applyTextAreaHeight(this.textAreaHeight());
  }

  onEditorValueChange(value: string) {
    this.editorValue.set(value);
    this.processImages(value);
  }

  private applyTextAreaHeight(value: any) {
    const heightValue = typeof value === 'number' ? `${value}px` : value;

    const menuBarHeight = ($('.NgxEditor__MenuBar').outerHeight() || 0) + 4;
    const totalHeight =
      typeof value === 'number' ? value : parseFloat(value) || 0;
    const contentHeight =
      totalHeight > menuBarHeight ? totalHeight - menuBarHeight : totalHeight;
    const contentHeightValue = `${contentHeight}px`;

    $('.ProseMirror.NgxEditor__Content').css('min-height', contentHeightValue);
    $('.NgxEditor').css('min-height', contentHeightValue);
  }

  onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;

    if (items) {
      for (let i = 0; i < items.length; i++) {
        // Controlla se l'elemento incollato è un'immagine
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();

          if (file) {
            // Impediamo il comportamento di default (che inserirebbe il path locale)
            event.preventDefault();

            const reader = new FileReader();
            reader.onload = (e: any) => {
              const base64Image = e.target.result;
              // Inseriamo l'immagine direttamente nell'HTML come stringa Base64
              this.insertImageAtCursor(base64Image);
            };
            reader.readAsDataURL(file);
          }
        }
      }
    }
  }

  private insertImageAtCursor(base64Image: string): void {
    const currentHtml = this.editorValue() ?? '';
    const imgTag = `<img src="${base64Image}" alt="Pasted Image" />`;

    // Ottiene la selezione corrente dall'editor ProseMirror
    const view = this.editor.view;
    if (view) {
      const { state, dispatch } = view;
      const { tr, schema } = state;

      // Crea un nodo immagine
      const imageNode = schema.nodes['image']?.create({
        src: base64Image,
        alt: 'Pasted Image',
      });

      if (imageNode) {
        // Inserisce l'immagine alla posizione del cursore
        const transaction = tr.replaceSelectionWith(imageNode);
        dispatch(transaction);
        this._allowUpdate = true;
      } else {
        // Fallback: aggiungi l'immagine alla fine del contenuto
        const newHtml = currentHtml + imgTag;
        this.editor.setContent(newHtml);
        this.editorValue.set(newHtml);
        this._allowUpdate = true;
        this.processImages(newHtml);
      }
    } else {
      // Fallback: aggiungi l'immagine alla fine del contenuto
      const newHtml = currentHtml + imgTag;
      this.editor.setContent(newHtml);
      this.editorValue.set(newHtml);
      this._allowUpdate = true;
      this.processImages(newHtml);
    }
  }
}
